import Alpine from 'alpinejs'
import { gsap } from 'gsap'

const desktopMediaQuery = window.matchMedia('(min-width: 1024px)')
const lenisLockMediaQuery = window.matchMedia('(max-width: 1023px)')
let hasUserInteractedWithMenu = false
const OPEN_SLIDE_DURATION = 0.6
const CLOSE_SLIDE_DURATION = 0.6
const ITEM_OPEN_DURATION = 0.4
const ITEM_CLOSE_DURATION = 0.22
const ITEM_OPEN_START = 0.16
const ITEM_CLOSE_START = 0
const ITEM_OPEN_STAGGER = 0.07
const ITEM_CLOSE_STAGGER = 0.02
const MOBILE_BG_DURATION = 0.6
const MOBILE_BG_STAGGER = 0.15
const MOBILE_ITEMS_DURATION = 0.4
const MOBILE_ITEMS_STAGGER = 0.03
const MOBILE_ITEMS_START = 0.28
const MOBILE_ITEMS_CLOSED_Y = 110
const MOBILE_FADE_DURATION = 0.3
const MOBILE_FADE_START = 0.34
let isMenuStoreRegistered = false

const getMenuOpenState = () => {
    const menuStore = Alpine.store('menu')
    return menuStore?.open ?? false
}

const syncLenisPrevent = (open = false) => {
    if (open && lenisLockMediaQuery.matches) {
        document.body.setAttribute('data-lenis-prevent', '')
        return
    }

    document.body.removeAttribute('data-lenis-prevent')
}

const syncDesktopMenuAnimation = (
    open = false,
    { instant = false, root = document } = {}
) => {
    const navContainers = root.querySelectorAll('.js-navContainer')

    navContainers.forEach((navContainer) => {
        const metaNav = navContainer.querySelector('.js-metaNav')
        const metaNavItems = navContainer.querySelectorAll('.js-metaNav-item')

        if (!metaNav) {
            return
        }

        gsap.killTweensOf([navContainer, ...metaNavItems])

        if (!desktopMediaQuery.matches) {
            gsap.set(navContainer, { x: 0, clearProps: 'transform' })
            gsap.set(metaNavItems, { yPercent: 0 })
            return
        }

        const metaNavWidth = metaNav.getBoundingClientRect().width
        const containerX = open ? 0 : metaNavWidth
        const itemsY = open ? 0 : 100

        if (instant) {
            gsap.set(navContainer, { x: containerX })
            gsap.set(metaNavItems, { yPercent: itemsY })
            return
        }

        const timeline = gsap.timeline({
            defaults: { overwrite: 'auto' }
        })

        timeline.to(
            navContainer,
            {
                x: containerX,
                duration: open ? OPEN_SLIDE_DURATION : CLOSE_SLIDE_DURATION,
                ease: open ? 'power3.out' : 'power2.inOut'
            },
            0
        )

        timeline.to(
            metaNavItems,
            {
                yPercent: itemsY,
                duration: open ? ITEM_OPEN_DURATION : ITEM_CLOSE_DURATION,
                ease: open ? 'power3.out' : 'power2.in',
                stagger: open ? ITEM_OPEN_STAGGER : ITEM_CLOSE_STAGGER
            },
            open ? ITEM_OPEN_START : ITEM_CLOSE_START
        )
    })
}

const syncMobileMenuAnimation = (
    open = false,
    { instant = false, root = document } = {}
) => {
    const mobileMenus = root.querySelectorAll('#main-menu')

    mobileMenus.forEach((mobileMenu) => {
        const bgGreen = mobileMenu.querySelector('.js-bg-green')
        const bgBlack = mobileMenu.querySelector('.js-bg-black')
        const mobileItems = mobileMenu.querySelectorAll('.js-mobileNav-item')
        const mobileFades = mobileMenu.querySelectorAll('.js-mobile-fade')
        const bgPanels = [bgGreen, bgBlack].filter(Boolean)

        gsap.killTweensOf([...bgPanels, ...mobileItems, ...mobileFades])

        if (desktopMediaQuery.matches) {
            gsap.set(bgPanels, { scaleX: 0, transformOrigin: 'right center' })
            gsap.set(mobileItems, { yPercent: 0 })
            gsap.set(mobileFades, { opacity: 1 })
            return
        }

        const shouldInstant = instant

        if (shouldInstant) {
            gsap.set(bgPanels, {
                scaleX: open ? 1 : 0,
                transformOrigin: 'right center'
            })
            gsap.set(mobileItems, {
                yPercent: open ? 0 : MOBILE_ITEMS_CLOSED_Y
            })
            gsap.set(mobileFades, { opacity: open ? 1 : 0 })
            return
        }

        const timeline = gsap.timeline({
            defaults: { overwrite: 'auto' }
        })

        if (open) {
            timeline.set(
                bgPanels,
                { scaleX: 0, transformOrigin: 'right center' },
                0
            )
            timeline.set(mobileItems, { yPercent: MOBILE_ITEMS_CLOSED_Y }, 0)
            timeline.set(mobileFades, { opacity: 0 }, 0)

            timeline.to(
                bgPanels,
                {
                    scaleX: 1,
                    transformOrigin: 'right center',
                    duration: MOBILE_BG_DURATION,
                    ease: 'power3.inOut',
                    stagger: MOBILE_BG_STAGGER
                },
                0
            )

            timeline.to(
                mobileItems,
                {
                    yPercent: 0,
                    duration: MOBILE_ITEMS_DURATION,
                    ease: 'power3.out',
                    stagger: MOBILE_ITEMS_STAGGER
                },
                '<+=0.6'
            )

            timeline.to(
                mobileFades,
                {
                    opacity: 1,
                    duration: MOBILE_FADE_DURATION,
                    ease: 'power2.out'
                },
                '<+=0.3'
            )
            return
        }

        timeline.to(
            mobileItems,
            {
                yPercent: MOBILE_ITEMS_CLOSED_Y,
                duration: MOBILE_ITEMS_DURATION * 0.8,
                ease: 'power2.in',
                stagger: MOBILE_ITEMS_STAGGER
            },
            0
        )

        timeline.to(
            bgPanels,
            {
                scaleX: 0,
                transformOrigin: 'right center',
                duration: MOBILE_BG_DURATION * 0.8,
                ease: 'power2.inOut',
                stagger: MOBILE_BG_STAGGER
            },
            0.08
        )

        timeline.to(
            mobileFades,
            {
                opacity: 0,
                duration: MOBILE_FADE_DURATION * 0.75,
                ease: 'power2.in'
            },
            0
        )
    })
}

const syncMenuAnimations = (open = false, options = {}) => {
    syncDesktopMenuAnimation(open, options)
    syncMobileMenuAnimation(open, options)
}

const initMenu = () => {
    return new Promise((resolve) => {
        const syncFromStore = () => {
            const isOpen = getMenuOpenState()

            syncLenisPrevent(isOpen)
            syncMenuAnimations(isOpen, {
                instant: !hasUserInteractedWithMenu,
                root: document
            })
        }

        if (!isMenuStoreRegistered) {
            document.addEventListener('click', (event) => {
                const mobileNavItem = event.target.closest('.js-mobileNav-item')
                if (!mobileNavItem || desktopMediaQuery.matches) return

                const menuStore = Alpine.store('menu')
                if (menuStore?.open) {
                    menuStore.close()
                }
            })

            Alpine.store('menu', {
                init() {},
                open: false,
                close() {
                    this.open = false
                    hasUserInteractedWithMenu = true
                    syncLenisPrevent(this.open)
                    syncMenuAnimations(this.open, { root: document })
                },
                toggle() {
                    this.open = !this.open
                    hasUserInteractedWithMenu = true
                    syncLenisPrevent(this.open)
                    syncMenuAnimations(this.open, { root: document })
                }
            })

            window.addEventListener('resize', syncFromStore)
            desktopMediaQuery.addEventListener('change', syncFromStore)
            window.addEventListener('load', syncFromStore)
            isMenuStoreRegistered = true
        }

        syncMenuAnimations(getMenuOpenState(), {
            instant: !hasUserInteractedWithMenu,
            root: document
        })
        syncLenisPrevent(getMenuOpenState())

        if (document.fonts?.ready) {
            document.fonts.ready.then(syncFromStore)
        }

        return resolve('menu done')
    })
}

const syncMenuLayout = ({ root = document, open, instant = true } = {}) => {
    const isOpen = typeof open === 'boolean' ? open : getMenuOpenState()
    syncMenuAnimations(isOpen, { instant, root })
    syncLenisPrevent(isOpen)
}

export { initMenu, syncMenuLayout }
